/**
 * Group Reactivity Lab (Year 10): reviewed trends for the alkali metals (group 1) with water and
 * for the halogens (group 17) in displacement reactions. Reactivity trends are explained with
 * electron shells. Rubidium and caesium are prediction-only and never done in schools.
 */
import { firstModelError, type ModelField } from './modelInputContract';
import { formatSpecies, isBalanced, parseFormula, type EquationTerm } from './reactionPredictionData';

export type GroupReactivityMode = 'alkali' | 'halogens';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const GROUP_REACTIVITY_TOOL_IDENTITY = {
  id: 'group-reactivity',
  toolKey: 'chemistry-group-reactivity',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const GROUP_REACTIVITY_START_YEAR = 10;

export const GROUP_REACTIVITY_MODES: readonly GroupReactivityMode[] = ['alkali', 'halogens'];

/**
 * Default-mode ladder for `modeForYear()` (CT-MODE). Both modes open from Year 10 and the alkali
 * metals are usually taught first, so every Year defaults to `alkali`; the learner switches freely.
 */
export const GROUP_DEFAULT_MODE_LADDER: readonly { mode: GroupReactivityMode; fromYear: number }[] = [
  { mode: 'alkali', fromYear: GROUP_REACTIVITY_START_YEAR },
];

export interface GroupReactivityProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: 'pending-teaching-review';
}

export const GROUP_REACTIVITY_PROVENANCE: GroupReactivityProvenance = {
  sources: [
    'CRC Handbook of Chemistry and Physics — melting and boiling points, densities and first ionisation energies (rounded)',
    'IUPAC periodic table and NIST Atomic Spectra Database — electron arrangements and ionisation energies',
    'Metallic radii (group 1) and covalent radii (group 17) as tabulated in standard senior chemistry texts (rounded to whole pm)',
    'Standard school demonstrations of group 1 metals with water and halogen displacement, as described in common Australian Year 10 science texts',
  ],
  conditions: 'Room temperature (about 25 °C) and 100 kPa. Halogen solutions are dilute aqueous solutions; halide solutions are potassium salts.',
  reviewStatus: 'pending-teaching-review',
};

// ---------------------------------------------------------------------------------------------
// Alkali metals
// ---------------------------------------------------------------------------------------------

export type AlkaliId = 'Li' | 'Na' | 'K' | 'Rb' | 'Cs';
export type AlkaliObservationId = 'steady-fizz' | 'melts-darts' | 'lilac-flame' | 'violent';

export const ALKALI_OBSERVATION_LABELS: Record<AlkaliObservationId, string> = {
  'steady-fizz': 'Floats and fizzes steadily; does not melt',
  'melts-darts': 'Floats, melts into a silvery ball and darts about',
  'lilac-flame': 'Melts, darts quickly and the hydrogen burns with a lilac flame',
  violent: 'Reacts violently or explosively at once',
};

/** Observation levels in order of increasing vigour. */
export const ALKALI_OBSERVATION_ORDER: AlkaliObservationId[] = ['steady-fizz', 'melts-darts', 'lilac-flame', 'violent'];

export interface AlkaliMetal {
  id: AlkaliId;
  name: string;
  atomicNumber: number;
  shells: number[];
  /** Metallic radius in pm. */
  radiusPm: number;
  /** First ionisation energy in kJ/mol. */
  firstIonisationKjMol: number;
  meltingPointC: number;
  densityGcm3: number;
  /** True only for metals a teacher may demonstrate in a school laboratory. */
  schoolDemo: boolean;
  observation: AlkaliObservationId;
  observations: string[];
}

const WATER_DENSITY_G_CM3 = 1;

export const ALKALI_METALS: AlkaliMetal[] = [
  {
    id: 'Li', name: 'lithium', atomicNumber: 3, shells: [2, 1], radiusPm: 152, firstIonisationKjMol: 520, meltingPointC: 181, densityGcm3: 0.53, schoolDemo: true,
    observation: 'steady-fizz',
    observations: ['Floats on the water.', 'Fizzes steadily and moves slowly.', 'Does not melt; slowly gets smaller.', 'Universal indicator in the water turns purple (alkaline).'],
  },
  {
    id: 'Na', name: 'sodium', atomicNumber: 11, shells: [2, 8, 1], radiusPm: 186, firstIonisationKjMol: 496, meltingPointC: 98, densityGcm3: 0.97, schoolDemo: true,
    observation: 'melts-darts',
    observations: ['Floats on the water.', 'Fizzes rapidly.', 'Melts into a shiny silver ball (the reaction releases enough heat to pass its 98 °C melting point).', 'Darts around the surface and disappears.', 'Universal indicator turns purple (alkaline).'],
  },
  {
    id: 'K', name: 'potassium', atomicNumber: 19, shells: [2, 8, 8, 1], radiusPm: 227, firstIonisationKjMol: 419, meltingPointC: 63.5, densityGcm3: 0.86, schoolDemo: true,
    observation: 'lilac-flame',
    observations: ['Floats on the water.', 'Melts at once and darts about very quickly.', 'The hydrogen ignites with a lilac flame.', 'May spit or crackle at the end.', 'Universal indicator turns purple (alkaline).'],
  },
  {
    id: 'Rb', name: 'rubidium', atomicNumber: 37, shells: [2, 8, 18, 8, 1], radiusPm: 248, firstIonisationKjMol: 403, meltingPointC: 39, densityGcm3: 1.53, schoolDemo: false,
    observation: 'violent',
    observations: ['Predicted from the trend: reacts violently at once.', 'Denser than water, so it sinks.', 'The hydrogen ignites immediately.'],
  },
  {
    id: 'Cs', name: 'caesium', atomicNumber: 55, shells: [2, 8, 18, 18, 8, 1], radiusPm: 265, firstIonisationKjMol: 376, meltingPointC: 28, densityGcm3: 1.93, schoolDemo: false,
    observation: 'violent',
    observations: ['Predicted from the trend: reacts explosively.', 'Denser than water, so it sinks.', 'Can shatter the container.'],
  },
];

export const PREDICTION_ONLY_NOTICE =
  'Prediction only: rubidium and caesium are never reacted with water in schools. Their behaviour is predicted from the group trend.';

export function alkaliById(id: AlkaliId): AlkaliMetal {
  const metal = ALKALI_METALS.find((entry) => entry.id === id);
  if (!metal) throw new Error(`Unknown alkali metal: ${id}`);
  return metal;
}

export function floatsOnWater(metal: AlkaliMetal): boolean {
  return metal.densityGcm3 < WATER_DENSITY_G_CM3;
}

/** 2M(s) + 2H₂O(l) → 2MOH(aq) + H₂(g) */
export function alkaliWaterEquation(metal: AlkaliMetal): { reactants: EquationTerm[]; products: EquationTerm[] } {
  return {
    reactants: [
      { coefficient: 2, species: `${metal.id}(s)` },
      { coefficient: 2, species: 'H2O(l)' },
    ],
    products: [
      { coefficient: 2, species: `${metal.id}OH(aq)` },
      { coefficient: 1, species: 'H2(g)' },
    ],
  };
}

export function alkaliWordEquation(metal: AlkaliMetal): string {
  return `${metal.name} + water → ${metal.name} hydroxide + hydrogen`;
}

export function formatEquation(terms: EquationTerm[]): string {
  return terms
    .map((term) => `${term.coefficient === 1 ? '' : term.coefficient}${formatSpecies(term.species)}`)
    .join(' + ');
}

export function alkaliTrendExplanation(metal: AlkaliMetal): string {
  const outer = metal.shells.length;
  if (ALKALI_METALS.indexOf(metal) === 0) {
    return `${capitalise(metal.name)} has ${outer} occupied shells. Its single outer electron is in shell ${outer}, close to the nucleus and shielded by only ${outer - 1} inner shell${outer - 1 === 1 ? '' : 's'}. It is held more strongly than in the metals below it, so ${metal.name} is the least vigorous of the group 1 metals.`;
  }
  return `${capitalise(metal.name)} has ${outer} occupied shells. Its single outer electron is in shell ${outer}, further from the nucleus and shielded by ${outer - 1} inner shell${outer - 1 === 1 ? '' : 's'}. The weaker attraction means it loses that electron more easily, so it reacts more vigorously than the metals above it.`;
}

export function checkAlkaliObservation(metal: AlkaliMetal, choice: AlkaliObservationId): PredictionFeedback {
  const expected = ALKALI_OBSERVATION_ORDER.indexOf(metal.observation);
  const chosen = ALKALI_OBSERVATION_ORDER.indexOf(choice);
  if (chosen < 0) throw new Error(`Unknown observation: ${choice}`);
  if (chosen === expected) {
    return {
      correct: true,
      feedback: metal.schoolDemo
        ? `Correct. ${ALKALI_OBSERVATION_LABELS[choice]}. ${alkaliTrendExplanation(metal)}`
        : `Correct prediction. ${alkaliTrendExplanation(metal)} ${PREDICTION_ONLY_NOTICE}`,
    };
  }
  if (chosen < expected) {
    return {
      correct: false,
      feedback: `Too gentle. ${capitalise(metal.name)} is lower in group 1, so its outer electron is further from the nucleus and more shielded. It is lost more easily, so the reaction is more vigorous than that.`,
    };
  }
  return {
    correct: false,
    feedback: `Too vigorous. ${capitalise(metal.name)} is higher in group 1 than that behaviour suggests: its outer electron is closer to the nucleus and held more strongly, so the reaction is gentler.`,
  };
}

// ---------------------------------------------------------------------------------------------
// Halogens
// ---------------------------------------------------------------------------------------------

export type HalogenId = 'F' | 'Cl' | 'Br' | 'I';
export type DisplacingHalogenId = Exclude<HalogenId, 'F'>;

export interface Halogen {
  id: HalogenId;
  name: string;
  moleculeName: string;
  molecule: string;
  halideName: string;
  shells: number[];
  /** Covalent radius in pm. */
  radiusPm: number;
  meltingPointC: number;
  boilingPointC: number;
  stateAt25C: 'gas' | 'liquid' | 'solid';
  appearance: string;
  /** Colour of a dilute aqueous solution; absent for fluorine, which reacts with water. */
  solutionColour?: string;
}

export const HALOGENS: Halogen[] = [
  { id: 'F', name: 'fluorine', moleculeName: 'fluorine', molecule: 'F2', halideName: 'fluoride', shells: [2, 7], radiusPm: 71, meltingPointC: -220, boilingPointC: -188, stateAt25C: 'gas', appearance: 'Pale yellow gas' },
  { id: 'Cl', name: 'chlorine', moleculeName: 'chlorine', molecule: 'Cl2', halideName: 'chloride', shells: [2, 8, 7], radiusPm: 99, meltingPointC: -101, boilingPointC: -34, stateAt25C: 'gas', appearance: 'Pale yellow-green gas', solutionColour: 'very pale green' },
  { id: 'Br', name: 'bromine', moleculeName: 'bromine', molecule: 'Br2', halideName: 'bromide', shells: [2, 8, 18, 7], radiusPm: 114, meltingPointC: -7, boilingPointC: 59, stateAt25C: 'liquid', appearance: 'Dark red-brown liquid with orange-brown vapour', solutionColour: 'orange' },
  { id: 'I', name: 'iodine', moleculeName: 'iodine', molecule: 'I2', halideName: 'iodide', shells: [2, 8, 18, 18, 7], radiusPm: 133, meltingPointC: 114, boilingPointC: 184, stateAt25C: 'solid', appearance: 'Shiny grey-black solid with purple vapour', solutionColour: 'brown' },
];

export const DISPLACING_HALOGENS: DisplacingHalogenId[] = ['Cl', 'Br', 'I'];

export function halogenById(id: HalogenId): Halogen {
  const halogen = HALOGENS.find((entry) => entry.id === id);
  if (!halogen) throw new Error(`Unknown halogen: ${id}`);
  return halogen;
}

export function stateAt(halogen: Halogen, temperatureC: number): 'gas' | 'liquid' | 'solid' {
  if (temperatureC < halogen.meltingPointC) return 'solid';
  if (temperatureC < halogen.boilingPointC) return 'liquid';
  return 'gas';
}

/** Learner temperature for the halogen state explorer (CT-INTERACT input). */
export const HALOGEN_TEMPERATURE_FIELDS: readonly ModelField<'temperatureC'>[] = [
  { key: 'temperatureC', label: 'Temperature', unit: '°C', min: -250, max: 250, step: 1 },
];

export interface HalogenStateStep {
  id: HalogenId;
  state: 'gas' | 'liquid' | 'solid';
  /** For example "mp −7 °C ≤ 25 °C < bp 59 °C → liquid". */
  working: string;
}

function celsius(value: number): string {
  return `${value < 0 ? '−' : ''}${Math.abs(value)} °C`;
}

/**
 * State of every halogen at the learner's temperature, compared with the reviewed melting and
 * boiling points at 100 kPa. Throws the field message for a temperature outside −250 to 250 °C.
 */
export function halogenStatesAt(temperatureC: number): HalogenStateStep[] {
  const error = firstModelError(HALOGEN_TEMPERATURE_FIELDS, { temperatureC });
  if (error) throw new Error(error);
  const t = celsius(temperatureC);
  return HALOGENS.map((halogen) => {
    const state = stateAt(halogen, temperatureC);
    const mp = celsius(halogen.meltingPointC);
    const bp = celsius(halogen.boilingPointC);
    const working =
      state === 'solid'
        ? `${t} < mp ${mp} → solid`
        : state === 'liquid'
          ? `mp ${mp} ≤ ${t} < bp ${bp} → liquid`
          : `${t} ≥ bp ${bp} → gas`;
    return { id: halogen.id, state, working };
  });
}

export interface IonicTerm {
  coefficient: number;
  /** Formula without charge, for example `Br`. */
  formula: string;
  charge: number;
  state: 'aq';
}

export type DisplacementStatus = 'reaction' | 'no-reaction' | 'same-halogen';

export interface DisplacementCell {
  id: string;
  halogen: DisplacingHalogenId;
  halide: DisplacingHalogenId;
  status: DisplacementStatus;
  startColour: string;
  endColour: string;
  /** Full equation with the potassium salts (reactions only). */
  full?: { reactants: EquationTerm[]; products: EquationTerm[] };
  /** Ionic equation without spectator K⁺ ions (reactions only). */
  ionic?: { reactants: IonicTerm[]; products: IonicTerm[] };
  explanation: string;
}

export const MIN_DISPLACEMENT_COMBINATIONS = 6;

const ACTIVITY_ORDER: DisplacingHalogenId[] = ['Cl', 'Br', 'I'];

function buildCell(halogenId: DisplacingHalogenId, halideId: DisplacingHalogenId): DisplacementCell {
  const halogen = halogenById(halogenId);
  const halide = halogenById(halideId);
  const id = `${halogen.molecule}-K${halideId}`;
  const startColour = halogen.solutionColour ?? '';
  if (halogenId === halideId) {
    return {
      id,
      halogen: halogenId,
      halide: halideId,
      status: 'same-halogen',
      startColour,
      endColour: startColour,
      explanation: `${capitalise(halogen.name)} and ${halide.halideName} ions are the same element, so nothing is displaced.`,
    };
  }
  const reacts = ACTIVITY_ORDER.indexOf(halogenId) < ACTIVITY_ORDER.indexOf(halideId);
  if (!reacts) {
    return {
      id,
      halogen: halogenId,
      halide: halideId,
      status: 'no-reaction',
      startColour,
      endColour: startColour,
      explanation: `${capitalise(halogen.name)} is less reactive than ${halide.name}, so it cannot take the electron from ${halide.halideName} ions. The ${startColour} colour of ${halogen.name} stays.`,
    };
  }
  const x2 = halogen.molecule;
  const y2 = halide.molecule;
  return {
    id,
    halogen: halogenId,
    halide: halideId,
    status: 'reaction',
    startColour,
    endColour: halide.solutionColour ?? '',
    full: {
      reactants: [
        { coefficient: 1, species: `${x2}(aq)` },
        { coefficient: 2, species: `K${halideId}(aq)` },
      ],
      products: [
        { coefficient: 2, species: `K${halogenId}(aq)` },
        { coefficient: 1, species: `${y2}(aq)` },
      ],
    },
    ionic: {
      reactants: [
        { coefficient: 1, formula: x2, charge: 0, state: 'aq' },
        { coefficient: 2, formula: halideId, charge: -1, state: 'aq' },
      ],
      products: [
        { coefficient: 2, formula: halogenId, charge: -1, state: 'aq' },
        { coefficient: 1, formula: y2, charge: 0, state: 'aq' },
      ],
    },
    explanation: `${capitalise(halogen.name)} is more reactive than ${halide.name}: its atoms are smaller, so the nucleus attracts an extra electron more strongly. ${capitalise(halogen.name)} takes electrons from ${halide.halideName} ions and ${halide.name} forms, turning the solution ${halide.solutionColour}.`,
  };
}

export const DISPLACEMENT_GRID: DisplacementCell[] = DISPLACING_HALOGENS.flatMap((halogen) =>
  DISPLACING_HALOGENS.map((halide) => buildCell(halogen, halide)),
);

export function displacementCell(halogen: DisplacingHalogenId, halide: DisplacingHalogenId): DisplacementCell {
  const cell = DISPLACEMENT_GRID.find((entry) => entry.halogen === halogen && entry.halide === halide);
  if (!cell) throw new Error(`Unknown combination: ${halogen} with ${halide}`);
  return cell;
}

export function ionicTermsBalanced(reactants: IonicTerm[], products: IonicTerm[]): boolean {
  const totals: Record<string, number> = {};
  let charge = 0;
  const add = (terms: IonicTerm[], sign: number) =>
    terms.forEach((term) => {
      Object.entries(parseFormula(term.formula)).forEach(([element, count]) => {
        totals[element] = (totals[element] ?? 0) + sign * term.coefficient * count;
      });
      charge += sign * term.coefficient * term.charge;
    });
  add(reactants, 1);
  add(products, -1);
  return charge === 0 && Object.values(totals).every((total) => total === 0);
}

export function fullTermsBalanced(cell: DisplacementCell): boolean {
  return cell.full ? isBalanced(cell.full.reactants, cell.full.products) : false;
}

function formatIonicTerm(term: IonicTerm): string {
  const charge = term.charge === 0 ? '' : term.charge === -1 ? '⁻' : '⁺';
  return `${term.coefficient === 1 ? '' : term.coefficient}${formatSpecies(term.formula)}${charge}(${term.state})`;
}

export function formatIonicEquation(cell: DisplacementCell): string | null {
  if (!cell.ionic) return null;
  return `${cell.ionic.reactants.map(formatIonicTerm).join(' + ')} → ${cell.ionic.products.map(formatIonicTerm).join(' + ')}`;
}

export function formatFullEquation(cell: DisplacementCell): string | null {
  if (!cell.full) return null;
  return `${formatEquation(cell.full.reactants)} → ${formatEquation(cell.full.products)}`;
}

export function checkDisplacementPrediction(cell: DisplacementCell, predictsReaction: boolean): PredictionFeedback | null {
  if (cell.status === 'same-halogen') return null;
  const reacts = cell.status === 'reaction';
  if (predictsReaction === reacts) {
    return { correct: true, feedback: `Correct. ${cell.explanation}` };
  }
  const halogen = halogenById(cell.halogen);
  const halide = halogenById(cell.halide);
  return {
    correct: false,
    feedback: reacts
      ? `${capitalise(halogen.name)} is higher in group 17 than ${halide.name}, so it is more reactive and does displace it. ${cell.explanation}`
      : `A halogen only displaces a less reactive one. ${capitalise(halogen.name)} is lower in group 17 than ${halide.name}, so nothing happens and the colour does not change.`,
  };
}

export function halogenTrendExplanation(halogen: Halogen): string {
  const outer = halogen.shells.length;
  return `${capitalise(halogen.name)} has 7 electrons in shell ${outer} and needs to gain 1 more. The further the outer shell is from the nucleus, and the more inner shells shield it, the weaker the pull on an incoming electron. So reactivity decreases down group 17.`;
}

// ---------------------------------------------------------------------------------------------
// Prediction questions
// ---------------------------------------------------------------------------------------------

export interface PredictionFeedback {
  correct: boolean;
  feedback: string;
}

export interface GroupOption {
  id: string;
  label: string;
  feedback: string;
}

export interface GroupQuestion {
  id: string;
  mode: GroupReactivityMode;
  prompt: string;
  options: GroupOption[];
  correctOptionId: string;
}

export const MIN_QUESTIONS_PER_MODE = 8;

export const GROUP_QUESTIONS: GroupQuestion[] = [
  // Alkali metals
  {
    id: 'alkali-gas', mode: 'alkali', correctOptionId: 'hydrogen',
    prompt: 'Which gas forms when sodium reacts with water?',
    options: [
      { id: 'hydrogen', label: 'Hydrogen', feedback: 'Yes. Sodium takes the place of one hydrogen in water: 2Na + 2H₂O → 2NaOH + H₂. A lit splint gives a squeaky pop.' },
      { id: 'oxygen', label: 'Oxygen', feedback: 'No. The oxygen stays in the hydroxide (NaOH). The gas released is hydrogen.' },
      { id: 'carbon-dioxide', label: 'Carbon dioxide', feedback: 'No. Neither sodium nor water contains carbon, so no carbon dioxide can form.' },
      { id: 'steam', label: 'Only steam', feedback: 'The reaction is hot, but the bubbles are mainly hydrogen gas from the water molecules.' },
    ],
  },
  {
    id: 'alkali-product', mode: 'alkali', correctOptionId: 'hydroxide',
    prompt: 'What is the other product when potassium reacts with water?',
    options: [
      { id: 'hydroxide', label: 'Potassium hydroxide', feedback: 'Yes. 2K + 2H₂O → 2KOH + H₂. Potassium hydroxide dissolves and makes the water alkaline.' },
      { id: 'oxide', label: 'Potassium oxide', feedback: 'Not in water. Any oxide would react with water to form the hydroxide, KOH.' },
      { id: 'chloride', label: 'Potassium chloride', feedback: 'No. There is no chlorine in water. A chloride forms with hydrochloric acid or chlorine.' },
      { id: 'hydride', label: 'Potassium hydride', feedback: 'No. Hydrogen is released as a gas; potassium ends up as K⁺ in potassium hydroxide.' },
    ],
  },
  {
    id: 'alkali-indicator', mode: 'alkali', correctOptionId: 'purple',
    prompt: 'Universal indicator is added to the water before lithium goes in. What colour is it at the end?',
    options: [
      { id: 'purple', label: 'Purple (alkaline)', feedback: 'Yes. Lithium hydroxide is an alkali, so the pH rises well above 7.' },
      { id: 'red', label: 'Red (acidic)', feedback: 'No. Group 1 metals make hydroxides, which are alkalis, not acids. That is why they are called alkali metals.' },
      { id: 'green', label: 'Green (neutral)', feedback: 'No. The hydroxide formed makes the water alkaline, so the colour moves to blue or purple.' },
      { id: 'colourless', label: 'Colourless', feedback: 'Universal indicator always shows a colour. Phenolphthalein would turn pink here.' },
    ],
  },
  {
    id: 'alkali-most-vigorous', mode: 'alkali', correctOptionId: 'K',
    prompt: 'Which of lithium, sodium and potassium reacts most vigorously with water?',
    options: [
      { id: 'K', label: 'Potassium', feedback: 'Yes. Potassium is lowest of the three, so its outer electron is furthest from the nucleus and lost most easily.' },
      { id: 'Na', label: 'Sodium', feedback: 'Sodium is more vigorous than lithium, but potassium is lower in the group and more vigorous still.' },
      { id: 'Li', label: 'Lithium', feedback: 'No. Lithium is the least reactive of the three. Its outer electron is closest to the nucleus and held most strongly.' },
      { id: 'same', label: 'All the same', feedback: 'No. They all have one outer electron, but it is held less strongly further down the group, so reactivity increases.' },
    ],
  },
  {
    id: 'alkali-flame', mode: 'alkali', correctOptionId: 'lilac',
    prompt: 'Potassium on water sets the hydrogen alight. What colour is the flame?',
    options: [
      { id: 'lilac', label: 'Lilac', feedback: 'Yes. Potassium compounds give a lilac flame colour.' },
      { id: 'yellow', label: 'Yellow-orange', feedback: 'That is the flame colour of sodium, not potassium.' },
      { id: 'crimson', label: 'Crimson red', feedback: 'That is the flame colour of lithium. Potassium gives lilac.' },
      { id: 'green', label: 'Green', feedback: 'Green flames come from copper or barium compounds. Potassium gives lilac.' },
    ],
  },
  {
    id: 'alkali-why', mode: 'alkali', correctOptionId: 'distance',
    prompt: 'Why is potassium more reactive than sodium?',
    options: [
      { id: 'distance', label: 'Its outer electron is further from the nucleus and more shielded', feedback: 'Yes. Potassium (2, 8, 8, 1) has one more shell than sodium (2, 8, 1). The outer electron feels a weaker pull and is lost more easily.' },
      { id: 'more-outer', label: 'It has more electrons in its outer shell', feedback: 'No. Every group 1 metal has exactly one outer electron. The difference is how far that electron is from the nucleus.' },
      { id: 'stronger-pull', label: 'Its nucleus holds the outer electron more strongly', feedback: 'The opposite. A stronger hold would make the electron harder to lose and the metal less reactive.' },
      { id: 'heavier', label: 'It is heavier', feedback: 'Mass alone does not decide reactivity. What matters is how easily the outer electron is lost.' },
    ],
  },
  {
    id: 'alkali-float', mode: 'alkali', correctOptionId: 'density',
    prompt: 'Why do lithium, sodium and potassium float on water?',
    options: [
      { id: 'density', label: 'They are less dense than water', feedback: 'Yes. Their densities are about 0.53, 0.97 and 0.86 g/cm³, all below water’s 1.00 g/cm³.' },
      { id: 'gas', label: 'Only because the hydrogen bubbles lift them', feedback: 'Bubbles help them skate around, but a piece floats even before it reacts because it is less dense than water.' },
      { id: 'oil', label: 'They are coated in oil', feedback: 'They are stored under oil to keep air and water away, but they float because of their low density.' },
      { id: 'hollow', label: 'They are hollow', feedback: 'No. They are solid metals with unusually low densities, below 1.00 g/cm³.' },
    ],
  },
  {
    id: 'alkali-rubidium', mode: 'alkali', correctOptionId: 'more-violent',
    prompt: 'Predict (do not test): how would rubidium react with water compared with potassium?',
    options: [
      { id: 'more-violent', label: 'More violently', feedback: 'Yes, from the trend. Rubidium has one more shell, so its outer electron is lost even more easily. It is never done in schools.' },
      { id: 'similar', label: 'About the same', feedback: 'The trend continues: rubidium’s outer electron is further away, so the reaction is more violent.' },
      { id: 'gentler', label: 'More gently', feedback: 'No. Reactivity increases down group 1, and rubidium is below potassium.' },
      { id: 'none', label: 'No reaction', feedback: 'No. All group 1 metals react with water, and rubidium reacts violently.' },
    ],
  },
  {
    id: 'alkali-melting', mode: 'alkali', correctOptionId: 'decreases',
    prompt: 'Melting points: lithium 181 °C, sodium 98 °C, potassium 63.5 °C. What is the trend down the group?',
    options: [
      { id: 'decreases', label: 'Melting point decreases', feedback: 'Yes. Larger atoms mean weaker metallic bonding, so caesium melts at about 28 °C.' },
      { id: 'increases', label: 'Melting point increases', feedback: 'Check the numbers: 181 → 98 → 63.5 °C. The melting point falls down the group.' },
      { id: 'no-trend', label: 'No clear trend', feedback: 'The values fall steadily from 181 to 63.5 °C, so there is a clear decreasing trend.' },
      { id: 'same', label: 'They stay the same', feedback: 'No. They change a lot: lithium melts at 181 °C but potassium at 63.5 °C.' },
    ],
  },
  // Halogens
  {
    id: 'halogen-bromine-state', mode: 'halogens', correctOptionId: 'liquid',
    prompt: 'Bromine melts at −7 °C and boils at 59 °C. What is its state at 25 °C?',
    options: [
      { id: 'liquid', label: 'Liquid', feedback: 'Yes. 25 °C is above its melting point and below its boiling point. Bromine is a dark red-brown liquid.' },
      { id: 'gas', label: 'Gas', feedback: 'Not yet. Bromine boils at 59 °C, so at 25 °C it is a liquid (with some orange-brown vapour).' },
      { id: 'solid', label: 'Solid', feedback: 'No. 25 °C is well above its melting point of −7 °C.' },
      { id: 'mixture', label: 'Half solid, half gas', feedback: 'No. Between its melting and boiling points a substance is a liquid.' },
    ],
  },
  {
    id: 'halogen-chlorine-colour', mode: 'halogens', correctOptionId: 'yellow-green-gas',
    prompt: 'What does chlorine look like at room temperature?',
    options: [
      { id: 'yellow-green-gas', label: 'Pale yellow-green gas', feedback: 'Yes. Chlorine is a toxic pale yellow-green gas.' },
      { id: 'brown-liquid', label: 'Red-brown liquid', feedback: 'That is bromine. Chlorine boils at −34 °C, so it is a gas at room temperature.' },
      { id: 'grey-solid', label: 'Grey-black solid', feedback: 'That is iodine. Chlorine is a pale yellow-green gas.' },
      { id: 'colourless-gas', label: 'Colourless gas', feedback: 'Close for state, but chlorine has a pale yellow-green colour.' },
    ],
  },
  {
    id: 'halogen-boiling-trend', mode: 'halogens', correctOptionId: 'increases',
    prompt: 'What happens to boiling point down group 17 from fluorine to iodine?',
    options: [
      { id: 'increases', label: 'It increases', feedback: 'Yes: −188, −34, 59, 184 °C. Bigger molecules have stronger forces between them, so states change from gas to liquid to solid.' },
      { id: 'decreases', label: 'It decreases', feedback: 'The opposite. Fluorine and chlorine are gases but iodine is a solid, so boiling point rises down the group.' },
      { id: 'same', label: 'It stays about the same', feedback: 'No. Boiling point rises by over 370 °C from fluorine to iodine.' },
      { id: 'up-down', label: 'It rises, then falls', feedback: 'No. It rises at every step: −188, −34, 59, 184 °C.' },
    ],
  },
  {
    id: 'halogen-astatine', mode: 'halogens', correctOptionId: 'solid',
    prompt: 'Predict from the trend: what state is astatine, below iodine, at room temperature?',
    options: [
      { id: 'solid', label: 'Solid', feedback: 'Yes, from the trend. Iodine is already a solid and melting point rises down the group. Astatine is too radioactive to study in bulk, so this is a prediction.' },
      { id: 'liquid', label: 'Liquid', feedback: 'The trend goes gas → gas → liquid → solid. Below iodine, astatine is predicted to be a solid.' },
      { id: 'gas', label: 'Gas', feedback: 'No. Gases are at the top of the group. Melting and boiling points rise down group 17.' },
      { id: 'unknown', label: 'Cannot predict', feedback: 'You can use the group trend. Melting point rises down the group, so astatine is predicted to be a solid.' },
    ],
  },
  {
    id: 'halogen-most-reactive', mode: 'halogens', correctOptionId: 'F',
    prompt: 'Which halogen is the most reactive?',
    options: [
      { id: 'F', label: 'Fluorine', feedback: 'Yes. Fluorine has the smallest atoms, so its nucleus pulls an extra electron in most strongly.' },
      { id: 'Cl', label: 'Chlorine', feedback: 'Chlorine is very reactive, but fluorine is above it and more reactive still.' },
      { id: 'Br', label: 'Bromine', feedback: 'No. Reactivity decreases down group 17, and two halogens are above bromine.' },
      { id: 'I', label: 'Iodine', feedback: 'No. Iodine is the least reactive of these; its outer shell is furthest from the nucleus.' },
    ],
  },
  {
    id: 'halogen-why', mode: 'halogens', correctOptionId: 'gain-harder',
    prompt: 'Why does reactivity decrease down group 17?',
    options: [
      { id: 'gain-harder', label: 'The outer shell is further away, so gaining an electron is harder', feedback: 'Yes. Halogens react by gaining one electron. More shells mean more distance and shielding, so the attraction for the new electron is weaker.' },
      { id: 'lose-easier', label: 'Atoms lose electrons more easily', feedback: 'Halogens react by gaining an electron, not losing one. Losing electrons more easily is the group 1 explanation.' },
      { id: 'more-outer', label: 'They have more outer electrons', feedback: 'No. Every halogen has 7 outer electrons. The difference is the distance from the nucleus.' },
      { id: 'heavier', label: 'The molecules are heavier', feedback: 'Heavier molecules explain the rising boiling points, not the falling reactivity. Reactivity depends on gaining an electron.' },
    ],
  },
  {
    id: 'halogen-cl-ki', mode: 'halogens', correctOptionId: 'brown',
    prompt: 'Chlorine water is added to potassium iodide solution. What do you see?',
    options: [
      { id: 'brown', label: 'The solution turns brown', feedback: 'Yes. Chlorine is more reactive and displaces iodine: Cl₂ + 2I⁻ → 2Cl⁻ + I₂. Iodine in solution is brown.' },
      { id: 'no-change', label: 'No change', feedback: 'No. Chlorine is more reactive than iodine, so it does displace iodine and the colour changes.' },
      { id: 'precipitate', label: 'A white precipitate', feedback: 'No. Potassium chloride is soluble. The visible change is the brown colour of iodine.' },
      { id: 'green', label: 'Green gas bubbles off', feedback: 'No gas forms. The chlorine is used up as it displaces iodine, turning the solution brown.' },
    ],
  },
  {
    id: 'halogen-br-kcl', mode: 'halogens', correctOptionId: 'no-reaction',
    prompt: 'Bromine water is added to potassium chloride solution. What happens?',
    options: [
      { id: 'no-reaction', label: 'No reaction; it stays orange', feedback: 'Yes. Bromine is less reactive than chlorine, so it cannot displace chloride ions.' },
      { id: 'chlorine-forms', label: 'Chlorine forms', feedback: 'No. Only a more reactive halogen displaces a less reactive one. Bromine is below chlorine.' },
      { id: 'colourless', label: 'It turns colourless', feedback: 'No. Nothing reacts, so the orange bromine colour stays.' },
      { id: 'brown', label: 'It turns brown', feedback: 'Brown would mean iodine. There is no iodide here, and bromine cannot displace chloride.' },
    ],
  },
  {
    id: 'halogen-ionic', mode: 'halogens', correctOptionId: 'ionic',
    prompt: 'Which is the ionic equation for chlorine water with potassium bromide solution?',
    options: [
      { id: 'ionic', label: 'Cl₂ + 2Br⁻ → 2Cl⁻ + Br₂', feedback: 'Yes. K⁺ is a spectator ion and is left out. Atoms and charges (2− each side) balance.' },
      { id: 'unbalanced', label: 'Cl₂ + Br⁻ → Cl⁻ + Br₂', feedback: 'Not balanced. There are 2 Cl on the left but 1 on the right, and only 1 Br on the left.' },
      { id: 'spectator', label: 'Cl₂ + 2K⁺ → 2KCl', feedback: 'K⁺ is a spectator ion and does not change. The reaction is between chlorine and bromide ions.' },
      { id: 'reverse', label: 'Br₂ + 2Cl⁻ → 2Br⁻ + Cl₂', feedback: 'This is the reverse. Bromine is less reactive, so this reaction does not happen.' },
    ],
  },
];

export function questionsFor(mode: GroupReactivityMode): GroupQuestion[] {
  return GROUP_QUESTIONS.filter((question) => question.mode === mode);
}

export function checkGroupQuestion(question: GroupQuestion, optionId: string): PredictionFeedback | null {
  const option = question.options.find((candidate) => candidate.id === optionId);
  if (!option) return null;
  return { correct: optionId === question.correctOptionId, feedback: option.feedback };
}

function capitalise(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
